"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronDown, ArrowRight } from "lucide-react";
import Container from "@/components/ui/container";
import { productsInCollection, findCollection } from "@/lib/data";
import Price from "@/components/ui/price";
import { cn } from "@/lib/cn";

type NavItem = { label: string; href: string; handle?: string };

const items: NavItem[] = [
  { label: "Porcelain", href: "/collections/porcelain", handle: "porcelain" },
  { label: "Marble", href: "/collections/marble", handle: "marble" },
  { label: "Metro", href: "/collections/metro", handle: "metro" },
  { label: "Mosaic", href: "/collections/mosaic", handle: "mosaic" },
  { label: "Outdoor", href: "/collections/outdoor", handle: "outdoor" },
  { label: "Terracotta", href: "/collections/terracotta", handle: "terracotta" },
  { label: "Large-format", href: "/collections/large-format", handle: "large-format" },
  { label: "Wood-effect", href: "/collections/wood-effect", handle: "wood-effect" },
];

const CLOSE_DELAY_MS = 140;

export default function CategoryTabs() {
  const [openHandle, setOpenHandle] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const open = (handle: string | null) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenHandle(handle);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenHandle(null), CLOSE_DELAY_MS);
  };

  const openCollection = openHandle ? findCollection(openHandle) : null;
  const openProducts = openHandle ? productsInCollection(openHandle).slice(0, 4) : [];

  return (
    <nav
      aria-label="Browse by category"
      onMouseLeave={scheduleClose}
      className="hidden lg:block relative bg-[color:var(--color-surface-alt)] border-t border-[color:var(--color-border)]"
    >
      <Container>
        <ul className="flex items-center justify-center gap-1 xl:gap-2 h-12 overflow-x-auto">
          {items.map((item) => {
            const isOpen = openHandle === item.handle;
            return (
              <li
                key={item.label}
                className="shrink-0"
                onMouseEnter={() => item.handle && open(item.handle)}
                onFocus={() => item.handle && open(item.handle)}
              >
                <Link
                  href={item.href}
                  className={cn(
                    "inline-flex items-center gap-1 h-12 px-3 xl:px-4 text-sm font-medium transition-colors border-b-2",
                    isOpen
                      ? "text-[color:var(--color-primary)] border-[color:var(--color-primary)]"
                      : "text-[color:var(--color-text)] border-transparent hover:text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)]",
                  )}
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                >
                  {item.label}
                  <ChevronDown size={14} strokeWidth={2} className={cn("transition-transform", isOpen && "rotate-180")} />
                </Link>
              </li>
            );
          })}
        </ul>
      </Container>

      {openHandle && openCollection && (
        <div
          className="absolute left-0 right-0 top-full z-40 bg-[color:var(--color-surface)] border-t border-[color:var(--color-border)] shadow-lift"
          role="menu"
          aria-label={`${openCollection.title} products`}
          onMouseEnter={() => open(openHandle)}
          onMouseLeave={scheduleClose}
        >
          <Container>
            <div className="py-6">
              <div className="flex items-end justify-between gap-4 mb-5">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-medium text-[color:var(--color-primary)]">
                    {openCollection.title}
                  </div>
                  <p className="text-sm text-[color:var(--color-muted)] mt-1 max-w-[40rem] line-clamp-1">
                    {openCollection.description}
                  </p>
                </div>
                <Link
                  href={`/collections/${openCollection.handle}`}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-dark)]"
                >
                  View all
                  <ArrowRight size={14} />
                </Link>
              </div>

              {openProducts.length > 0 ? (
                <ul className="grid grid-cols-4 gap-4">
                  {openProducts.map((p) => (
                    <li key={p.id}>
                      <Link
                        href={`/products/${p.handle}`}
                        className="group flex gap-3 items-center"
                      >
                        <div className="relative h-16 w-16 shrink-0 overflow-hidden bg-[color:var(--color-surface-alt)] border border-[color:var(--color-border)]">
                          <Image
                            src={p.featuredImage.url}
                            alt={p.featuredImage.altText ?? p.title}
                            fill
                            sizes="64px"
                            className="object-cover transition-transform duration-300 group-hover:scale-[1.06]"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--color-muted)]">
                            {p.vendor}
                          </div>
                          <div className="text-sm font-semibold text-[color:var(--color-text)] line-clamp-2 leading-snug group-hover:text-[color:var(--color-primary)]">
                            {p.title}
                          </div>
                          <div className="mt-0.5">
                            <Price
                              amount={p.priceRange.minVariantPrice}
                              compareAt={p.compareAtPriceRange?.minVariantPrice ?? null}
                              size="sm"
                            />
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-[color:var(--color-muted)] py-4">
                  No products in this collection yet.
                </div>
              )}
            </div>
          </Container>
        </div>
      )}
    </nav>
  );
}
